// Copyright 2025, University of Maryland, All Rights Reserved
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

import type { KbSchema, KnowledgeBaseItem } from '../types'
import type { KnowledgeBaseSettings } from '../hooks/useKnowledgeBaseSettings'
import { ItemRow } from './ItemRow'

export interface ItemListUpdatePayload {
  content: string
  score: number | null
  fields?: Record<string, string> | null
}

interface ItemListProps {
  items: KnowledgeBaseItem[]
  settings: KnowledgeBaseSettings
  schema?: KbSchema | null
  saving: boolean
  readOnly: boolean
  disabled: boolean
  onUpdate: (item: KnowledgeBaseItem, payload: ItemListUpdatePayload) => Promise<void>
  onDelete: (item: KnowledgeBaseItem) => Promise<void>
  onError: (message: string) => void
}

export function ItemList({
  items,
  settings,
  schema,
  saving,
  readOnly,
  disabled,
  onUpdate,
  onDelete,
  onError,
}: ItemListProps): JSX.Element {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set())

  const handleEdit = useCallback((item: KnowledgeBaseItem) => {
    setEditingId(item.id)
  }, [])

  const handleCancelEdit = useCallback(() => {
    setEditingId(null)
  }, [])

  const handleSave = useCallback(async (item: KnowledgeBaseItem, payload: Parameters<ItemListProps['onUpdate']>[1]) => {
    await onUpdate(item, payload)
    setEditingId(null)
  }, [onUpdate])

  const handleDelete = useCallback((item: KnowledgeBaseItem) => {
    setHiddenIds(current => new Set(current).add(item.id))
    let resolved = false
    const restore = () => setHiddenIds(current => {
      const next = new Set(current)
      next.delete(item.id)
      return next
    })
    const undo = () => {
      if (resolved) return
      resolved = true
      restore()
    }
    const commit = () => {
      if (resolved) return
      resolved = true
      void onDelete(item).catch(err => {
        restore()
        onError(err instanceof Error ? err.message : 'Failed to delete item')
      })
    }

    toast(`Item ${item.id} removed`, {
      duration: 5000,
      action: { label: 'Undo', onClick: undo },
      onAutoClose: commit,
      onDismiss: commit,
    })
  }, [onDelete, onError])

  return (
    <>
      <ol className="space-y-2.5" data-testid="kb-item-list">
        {items.filter(item => !hiddenIds.has(item.id)).map(item => (
          <ItemRow
            key={item.id}
            item={item}
            editing={editingId === item.id}
            readOnly={readOnly}
            disabled={disabled}
            saving={saving}
            settings={settings}
            schema={schema}
            onEdit={() => handleEdit(item)}
            onCancelEdit={handleCancelEdit}
            onSave={payload => handleSave(item, payload)}
            onDelete={() => handleDelete(item)}
            onError={onError}
          />
        ))}
      </ol>
    </>
  )
}
